import React from "react";
import { X, Table2 } from "lucide-react";
import { WHATSAPP_CFO } from "../../../data/cfoFraccionalData";

const formatCOP = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);

const formatPct = (value) => `${value >= 0 ? "" : ""}${value.toFixed(1)}%`;

function pctSobreVentas(valor, ventas) {
  if (!ventas) return "—";
  return formatPct((valor / ventas) * 100);
}

function celdaRentabilidad(valor) {
  const positivo = valor >= 0;
  return (
    <td
      className={`px-3 py-2 text-right font-bold tabular-nums ${
        positivo ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
      }`}
    >
      {formatCOP(valor)}
    </td>
  );
}

function celdaPctRentabilidad(valor, ventas) {
  const pct = ventas ? (valor / ventas) * 100 : 0;
  const positivo = pct >= 0;
  return (
    <td
      className={`px-3 py-2 text-right font-bold tabular-nums ${
        positivo ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
      }`}
    >
      {formatPct(pct)}
    </td>
  );
}

function FilaSubtotal({ label, cols, ventasRef, destacado = false }) {
  return (
    <tr className={destacado ? "bg-[#1746a2] text-white font-bold" : "bg-[#0b1f3f]/90 text-white font-semibold"}>
      <td className="px-3 py-2.5 sticky left-0 z-10 bg-inherit">{label}</td>
      {cols.map((v, i) => (
        <td key={i} className="px-3 py-2.5 text-right tabular-nums">
          {typeof v === "number" ? formatCOP(v) : v}
        </td>
      ))}
      <td className="px-3 py-2.5 text-right tabular-nums">
        {typeof cols[0] === "number" ? pctSobreVentas(cols[0], ventasRef) : "—"}
      </td>
    </tr>
  );
}

function FilaDato({ label, cols, ventasRef, ok = false }) {
  return (
    <tr className="border-b border-slate-200 hover:bg-slate-50/80">
      <td className="px-3 py-2 text-sm text-slate-800 sticky left-0 bg-white z-[1]">{label}</td>
      {cols.map((v, i) => (
        <td key={i} className="px-3 py-2 text-right text-sm tabular-nums text-slate-700">
          {ok ? (
            <span className="inline-block rounded px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold">
              Ok
            </span>
          ) : typeof v === "number" ? (
            formatCOP(v)
          ) : (
            v
          )}
        </td>
      ))}
      <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-600">
        {typeof cols[0] === "number" ? pctSobreVentas(cols[0], ventasRef) : "—"}
      </td>
    </tr>
  );
}

export function construirFilasTabla(snapshot, resultados, meses) {
  const { mcSin, mcCon } = resultados;

  const resumenBase = resultados.resumenBase ?? {};
  const resumenSin = mcSin.resumen ?? {};
  const resumenCon = mcCon.resumen ?? {};

  const unitFact = snapshot.ingresos;
  const ventasRef = resumenBase.facturacion ?? snapshot.ingresos * meses;

  return {
    ventasRef,
    filas: [
      {
        tipo: "subtotal",
        label: "Facturación total (horizonte 3 años)",
        cols: [resumenBase.facturacion, resumenSin.facturacion, resumenCon.facturacion],
        destacado: true,
      },
      {
        tipo: "dato",
        label: "Costes directos (variables + imprevistos)",
        cols: [
          resumenBase.costesDirectos,
          resumenSin.costesDirectos,
          resumenCon.costesDirectos,
        ],
      },
      {
        tipo: "dato",
        label: "Validación modelo costes directos",
        cols: [true, true, true],
        ok: true,
      },
      {
        tipo: "subtotal",
        label: "Coste directo total",
        cols: [resumenBase.costesDirectos, resumenSin.costesDirectos, resumenCon.costesDirectos],
      },
      {
        tipo: "dato",
        label: "Gastos generales (fijos operativos)",
        cols: [
          resumenBase.gastosGenerales,
          resumenSin.gastosGenerales,
          resumenCon.gastosGenerales,
        ],
      },
      {
        tipo: "subtotal",
        label: "Total costes operativos",
        cols: [
          resumenBase.costesOperativos,
          resumenSin.costesOperativos,
          resumenCon.costesOperativos,
        ],
      },
      {
        tipo: "dato",
        label: "Margen operativo bruto",
        cols: [resumenBase.margenOperativo, resumenSin.margenOperativo, resumenCon.margenOperativo],
      },
      {
        tipo: "dato",
        label: "% margen operativo s/ ventas",
        cols: [
          resumenBase.margenOperativoPct,
          resumenSin.margenOperativoPct,
          resumenCon.margenOperativoPct,
        ],
        esPct: true,
      },
      {
        tipo: "dato",
        label: "Gastos financieros (servicio de deuda)",
        cols: [
          resumenBase.gastosFinancieros,
          resumenSin.gastosFinancieros,
          resumenCon.gastosFinancieros,
        ],
      },
      {
        tipo: "subtotal",
        label: "Total gastos generales",
        cols: [
          resumenBase.totalGastos,
          resumenSin.totalGastos,
          resumenCon.totalGastos,
        ],
      },
      {
        tipo: "rentabilidad",
        label: "Rentabilidad / flujo acumulado",
        cols: [resumenBase.rentabilidad, resumenSin.rentabilidad, resumenCon.rentabilidad],
      },
      {
        tipo: "dato",
        label: "Saldo mínimo de caja proyectado",
        cols: [resumenBase.minSaldo, mcSin.minSaldoP10, mcCon.minSaldoP50],
      },
      {
        tipo: "dato",
        label: "Probabilidad de caja negativa (MC)",
        cols: [
          "—",
          `${(mcSin.probCajaNegativa * 100).toFixed(0)}%`,
          `${(mcCon.probCajaNegativa * 100).toFixed(0)}%`,
        ],
      },
      {
        tipo: "dato",
        label: "Impacto acumulado imprevistos",
        cols: [
          resumenBase.impactoImprevistos,
          resumenSin.impactoImprevistos,
          resumenCon.impactoImprevistos,
        ],
      },
    ],
    unitFact,
  };
}

export default function TablaCalculosEscenarios({ open, onClose, snapshot, resultados, meses }) {
  if (!open || !snapshot || !resultados) return null;

  const { ventasRef, filas, unitFact } = construirFilasTabla(snapshot, resultados, meses);
  const columnas = ["Unitario / mes", "Escenario base", "Sin planeación (MC p10)", "Con gestión (MC p50)"];

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-labelledby="tabla-calculos-titulo"
        className="relative w-full max-w-5xl my-4 rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 bg-[#0b1f3f] px-5 py-4 text-white">
          <div className="flex items-center gap-3">
            <Table2 className="w-6 h-6 text-[#93c5fd]" />
            <div>
              <p className="text-xs uppercase tracking-widest text-[#93c5fd] font-bold">Detalle financiero</p>
              <h2 id="tabla-calculos-titulo" className="text-lg sm:text-xl font-extrabold">
                Situación proyectada — escenarios a 3 años
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs sm:text-sm text-slate-600">
          Horizonte <strong>{meses} meses</strong> · Monte Carlo con imprevistos aleatorios · Cifras en COP.
          La columna <em>Sin planeación</em> refleja el percentil 10 (escenario adverso típico);{" "}
          <em>Con gestión</em>, la mediana con reservas y cobranza activa.
        </div>

        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#1746a2] text-white">
                <th className="px-3 py-3 text-left font-bold sticky left-0 z-20 bg-[#1746a2] min-w-[220px]">
                  Concepto
                </th>
                {columnas.map((c) => (
                  <th key={c} className="px-3 py-3 text-right font-bold whitespace-nowrap">
                    {c}
                  </th>
                ))}
                <th className="px-3 py-3 text-right font-bold whitespace-nowrap">% s/ ventas</th>
              </tr>
            </thead>
            <tbody>
              <FilaDato
                label="Facturación mensual de referencia"
                cols={[unitFact, snapshot.ingresos, snapshot.ingresos, snapshot.ingresos]}
                ventasRef={ventasRef}
              />
              {filas.map((fila, idx) => {
                if (fila.tipo === "subtotal") {
                  return (
                    <FilaSubtotal
                      key={idx}
                      label={fila.label}
                      cols={fila.cols}
                      ventasRef={ventasRef}
                      destacado={fila.destacado}
                    />
                  );
                }
                if (fila.tipo === "rentabilidad") {
                  return (
                    <tr key={idx} className="bg-[#1746a2]/10 font-bold border-y-2 border-[#1746a2]/30">
                      <td className="px-3 py-2.5 sticky left-0 bg-[#1746a2]/10 z-[1]">{fila.label}</td>
                      {fila.cols.map((v, i) => (
                        <React.Fragment key={i}>{celdaRentabilidad(v)}</React.Fragment>
                      ))}
                      {celdaPctRentabilidad(fila.cols[0], ventasRef)}
                    </tr>
                  );
                }
                if (fila.esPct) {
                  return (
                    <tr key={idx} className="border-b border-slate-200">
                      <td className="px-3 py-2 text-sm sticky left-0 bg-white z-[1]">{fila.label}</td>
                      {fila.cols.map((v, i) => (
                        <td key={i} className="px-3 py-2 text-right text-sm tabular-nums">
                          {formatPct(v)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right text-sm">—</td>
                    </tr>
                  );
                }
                return (
                  <FilaDato
                    key={idx}
                    label={fila.label}
                    cols={fila.cols}
                    ventasRef={ventasRef}
                    ok={fila.ok}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 bg-gradient-to-r from-[#0b1f3f] to-[#1746a2] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm leading-relaxed max-w-xl">
            Este nivel de detalle — escenarios, bandas de confianza e imprevistos — requiere modelado
            financiero continuo. La <strong>dirección de innovación financiera</strong> traduce estos números en decisiones
            de caja, pricing y reservas.
          </p>
          <a
            href={WHATSAPP_CFO}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white hover:bg-[#1da851] transition text-center"
          >
            Solicitar asesoría CFO
          </a>
        </div>
      </div>
    </div>
  );
}
